import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getSiteName, useContent } from "../lib/content";

const FALLBACK_LINKS = [
  { label: "Work", href: "/#work" },
  { label: "Studio", href: "/#studio" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { content } = useContent();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const logoMain = getSiteName(content);
  const logoSub = content?.nav_logo_sub ?? "Studio / 2026";
  const logoImage = content?.nav_logo_image_url || "";
  const links = content?.nav_links ?? FALLBACK_LINKS;
  const contactLabel = content?.nav_contact_label ?? "Contact";
  const contactEmail = content?.nav_contact_email ?? "hello@archdigital.studio";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-ink-900/70 border-b border-bone-50/5"
          : "bg-transparent"
      }`}
    >
      <div className="container flex items-center justify-between py-5">
        <Link
          to="/"
          data-testid="nav-logo"
          className="group flex items-center gap-3"
        >
          {logoImage ? (
            <img
              src={logoImage}
              alt={logoMain}
              data-testid="nav-logo-image"
              className="h-9 w-auto object-contain"
            />
          ) : (
            <div className="h-8 w-8 border border-bone-50/20 flex items-center justify-center">
              <span className="font-display text-sm tracking-tightest">
                {(logoMain || "A").charAt(0)}
              </span>
            </div>
          )}
          {(logoMain || logoSub) && (
            <div className="leading-none">
              {logoMain && (
                <div className="font-display text-xl tracking-tightest">
                  {logoMain}
                </div>
              )}
              {logoSub && (
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone-200/50 mt-1">
                  {logoSub}
                </div>
              )}
            </div>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-10 font-mono text-xs uppercase tracking-[0.2em] text-bone-200/70">
          {links.map((l, i) => (
            <a
              key={`${l.href}-${i}`}
              href={l.href}
              data-testid={`nav-link-${i}`}
              className="hover:text-bone-50 transition-colors"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/admin"
            data-testid="nav-admin"
            className="hover:text-ember transition-colors"
          >
            Admin
          </Link>
        </nav>

        <a
          href={`mailto:${contactEmail}`}
          data-testid="nav-contact"
          className="site-button hidden md:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] border border-bone-50/20 px-4 py-2 hover:border-ember hover:text-ember transition-colors"
        >
          <span>{contactLabel}</span>
          <span aria-hidden>↗</span>
        </a>
      </div>
    </header>
  );
}
